import { NavLink, useLocation } from "react-router-dom";
import "./Header.css";
import { useState, useEffect, useRef } from "react";

const projectLinks = [
  { to: "/architecture", label: "Architecture" },
  { to: "/interior", label: "Interior" },
  { to: "/project-management", label: "Project Management" },
 
];

const blogLinks = [
  { to: "/blogs", label: "Blog Posts" },
  { to: "/magazine", label: "Magazine" },
];

function Header() {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdownName, setOpenDropdownName] = useState(null);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 1025 : false
  );
  const dropdownRefs = useRef([]);
  const isMenuOpenRef = useRef(false);
  const isMobileRef = useRef(isMobile);
  const isProjectsDropdownOpen = openDropdownName === "projects";
  const isBlogsDropdownOpen = openDropdownName === "blogs";
  const isBlogsActive =
    location.pathname === "/blogs" ||
    location.pathname.startsWith("/blog/") ||
    location.pathname === "/magazine";

  useEffect(() => {
    isMenuOpenRef.current = isMenuOpen;
  }, [isMenuOpen]);

  useEffect(() => {
    isMobileRef.current = isMobile;
  }, [isMobile]);

  useEffect(() => {
    let rafId = null;

    const handleResize = () => {
      const nextIsMobile = window.innerWidth < 1025;
      setIsMobile(nextIsMobile);

      if (!nextIsMobile) {
        isMenuOpenRef.current = false;
        setIsMenuOpen(false);
        setOpenDropdownName(null);
      }
    };

    const handleScroll = () => {
      if (!isMenuOpenRef.current || !isMobileRef.current) return;

      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }

      rafId = window.requestAnimationFrame(() => {
        isMenuOpenRef.current = false;
        setIsMenuOpen(false);
        setOpenDropdownName(null);
      });
    };

    window.addEventListener("resize", handleResize, { passive: true });
    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleScroll);
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRefs.current.every(
          (dropdown) => !dropdown || !dropdown.contains(event.target)
        )
      ) {
        setOpenDropdownName(null);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpenDropdownName(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const toggleDropdown = (event, dropdownName) => {
    event.stopPropagation();
    setOpenDropdownName((currentName) =>
      currentName === dropdownName ? null : dropdownName
    );
  };

  const openDropdown = (dropdownName) => {
    if (isMobileRef.current) return;
    setOpenDropdownName(dropdownName);
  };

  const closeDropdown = () => {
    if (isMobileRef.current) return;
    setOpenDropdownName(null);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    setOpenDropdownName(null);
  };

  return (
    <header className="site-header">
      <nav className="navbar navbar-expand-lg site-navbar">
        <div className="container-fluid">
          <NavLink className="navbar-brand site-brand" to="/">
            URBAN iNFiLL
          </NavLink>

          <button
            className="navbar-toggler"
            type="button"
            onClick={toggleMenu}
            aria-controls="siteNavbar"
            aria-expanded={isMenuOpen}
            aria-label="Toggle navigation"
          >
            <span className="navbar-toggler-icon"></span>
          </button>

          <div className={`collapse navbar-collapse${isMenuOpen ? " show" : ""}`} id="siteNavbar">
            <ul className="navbar-nav ms-auto mb-2 mb-lg-0 site-nav-list">
              <li className="nav-item">
                <NavLink
                  to="/"
                  end
                  className={({ isActive }) =>
                    `nav-link site-nav-link${isActive ? " is-active" : ""}`
                  }
                  onClick={closeMenu}
                >
                  Home
                </NavLink>
              </li>

              <li className="nav-item">
                <NavLink
                  to="/about"
                  className={({ isActive }) =>
                    `nav-link site-nav-link${isActive ? " is-active" : ""}`
                  }
                  onClick={closeMenu}
                >
                  About
                </NavLink>
              </li>

              <li
                ref={(element) => {
                  dropdownRefs.current[0] = element;
                }}
                className={`nav-item site-dropdown${isProjectsDropdownOpen ? " show" : ""}`}
                onMouseEnter={() => openDropdown("projects")}
                onMouseLeave={closeDropdown}
              >
                <button
                  type="button"
                  className="nav-link site-nav-link site-dropdown-toggle"
                  onClick={(event) => toggleDropdown(event, "projects")}
                  aria-expanded={isProjectsDropdownOpen}
                  aria-haspopup="true"
                >
                  Projects
                </button>

                <ul className={`site-dropdown-menu${isProjectsDropdownOpen ? " show" : ""}`}>
                  {projectLinks.map((link) => (
                    <li key={link.to}>
                      <NavLink
                        to={link.to}
                        className={({ isActive }) =>
                          `site-dropdown-link${isActive ? " is-active" : ""}`
                        }
                        onClick={closeMenu}
                      >
                        {link.label}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </li>

              <li className="nav-item">
                <NavLink
                  to="/luxury-homes"
                  className={({ isActive }) =>
                    `nav-link site-nav-link${isActive ? " is-active" : ""}`
                  }
                  onClick={closeMenu}
                >
                  Ui Homes
                </NavLink>
              </li>

              <li
                ref={(element) => {
                  dropdownRefs.current[1] = element;
                }}
                className={`nav-item site-dropdown${isBlogsDropdownOpen ? " show" : ""}`}
                onMouseEnter={() => openDropdown("blogs")}
                onMouseLeave={closeDropdown}
              >
                <button
                  type="button"
                  className={`nav-link site-nav-link site-dropdown-toggle${isBlogsActive ? " is-active" : ""}`}
                  onClick={(event) => toggleDropdown(event, "blogs")}
                  aria-expanded={isBlogsDropdownOpen}
                  aria-haspopup="true"
                >
                  Blogs
                </button>

                <ul className={`site-dropdown-menu${isBlogsDropdownOpen ? " show" : ""}`}>
                  {blogLinks.map((link) => (
                    <li key={link.to || link.href}>
                      {link.to ? (
                        <NavLink
                          to={link.to}
                          className={({ isActive }) =>
                            `site-dropdown-link${isActive ? " is-active" : ""}`
                          }
                          onClick={closeMenu}
                        >
                          {link.label}
                        </NavLink>
                      ) : (
                        <a
                          href={link.href}
                          className="site-dropdown-link"
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={closeMenu}
                        >
                          {link.label}
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </li>

              <li className="nav-item">
                <NavLink
                  to="/career"
                  className={({ isActive }) =>
                    `nav-link site-nav-link${isActive ? " is-active" : ""}`
                  }
                  onClick={closeMenu}
                >
                  Career
                </NavLink>
              </li>

              <li className="nav-item">
                <NavLink
                  to="/contact"
                  className={({ isActive }) =>
                    `nav-link site-nav-link${isActive ? " is-active" : ""}`
                  }
                  onClick={closeMenu}
                >
                  Contact Us
                </NavLink>
              </li>

             
            </ul>
          </div>
        </div>
      </nav>
    </header>
  );
}

export default Header;
